import { Controller } from "@hotwired/stimulus";

/**
 * Folds paginated day groups into one card per bucket. Entries group into a
 * fixed set of day buckets (today / yesterday / earlier), but each lazily-
 * paginated page renders its own card for the bucket it contains — so a bucket
 * like "earlier" that spans pages would show a fresh card per page. When a
 * page's bucket card connects and page one already opened a card for that
 * bucket, we move the new rows into it and drop the now-empty section.
 */
export default class InboxDayMergeController extends Controller {
  static targets = ["bucket"];

  declare bucketTargets: HTMLElement[];

  // The canonical card per bucket key — the first one to connect, which page
  // subsequent pages' matching buckets merge into.
  private canonical = new Map<string, HTMLElement>();

  bucketTargetConnected(bucket: HTMLElement): void {
    const key = bucket.dataset.bucket;
    if (!key) return;

    const existing = this.canonical.get(key);
    if (existing && existing !== bucket && existing.isConnected) {
      this.mergeInto(existing, bucket);
      return;
    }
    this.canonical.set(key, bucket);
  }

  bucketTargetDisconnected(bucket: HTMLElement): void {
    const key = bucket.dataset.bucket;
    if (key && this.canonical.get(key) === bucket) this.canonical.delete(key);
  }

  // Move a continuation card's rows into the canonical card (preserving order,
  // as pages arrive oldest-last) and remove the leftover section + its header.
  private mergeInto(canonical: HTMLElement, continuation: HTMLElement): void {
    while (continuation.firstElementChild) canonical.appendChild(continuation.firstElementChild);
    continuation.closest("section")?.remove();
  }
}
